/**
 * EditOrderPriceModal — modal for editing a resting order's price and/or
 * quantity. Filename kept (rather than renamed to EditOrderModal) to avoid
 * import churn; the component now covers both dimensions.
 *
 * Smart-route notes (parent-handled):
 *   - Kalshi: a pure quantity-shrink at the same price routes through the
 *     relay's queue-preserving /decrease endpoint. Any other change
 *     (price moved, qty increased, both) goes through cancel + new-place
 *     and loses queue position.
 *   - Polymarket: no /decrease equivalent, so every change is cancel +
 *     new-place. The hint copy below is conditioned on `venue` so traders
 *     see the right caveat for the order they're editing.
 *
 * Venue-agnostic on its own — parent provides the formatted marketLabel
 * and the venue-appropriate replace handler.
 */

import { useEffect, useState } from 'react';
import { Loader2, Minus, Plus } from 'lucide-react';
import { Modal } from '@/components/atoms/Modal';
import { EventStateWarning } from '@/components/atoms/EventStateWarning';
import { useOrderEventStateCertainty } from '@/hooks/ui/useEventStateCertainty';
import { cn } from '@/lib/infra/utils';
import { americanOdds } from '@/views/ValueDashboard/tradeModalBook';
import {
  effectivePriceForFeeAdjustedOdds,
  makerRateForTicker,
} from '@/lib/pricing/fees';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useFeesReady } from '@/services/hydraFees';
import { useMarketStore } from '@/stores/marketStore';
import { stepReplacePriceCents, validateReplacePriceCents } from '@/lib/pricing/subCent';
import { resolveTickCents } from '@/lib/polymarket/tickSize';

interface EditOrderPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the new integer cents (1–99), new contract qty (integer
   *  ≥ 1), and an options bag. `forceCancelReplace` (Kalshi-only) overrides
   *  the smart-route so a same-price qty-shrink cancels+places instead of
   *  going through /decrease — used when the trader explicitly wants to
   *  reset queue position. Polymarket replaces ignore the flag since they
   *  always cancel+place. Resolve on success, reject on failure with a
   *  user-readable error. */
  onReplace: (
    newPriceCents: number,
    newQuantity: number,
    options?: { forceCancelReplace?: boolean },
  ) => Promise<void>;
  /** Pre-formatted by the parent — e.g. "CHC @ SFG — MLB — 05-24" or
   *  whatever the venue's describeMarket helper yields. */
  marketLabel: string;
  /** The market's named side that the order is on. Renders as "YES" / "NO". */
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  remainingCount: number;
  /** Current resting price in cents (0–100 exclusive, up to 2 decimals on
   *  sub-cent-tick markets). Pre-fills the input. */
  currentPriceCents: number;
  /** Kalshi ticker for fee-adjusted odds labels. Polymarket can omit. */
  ticker?: string;
  /** Drives the hint-copy choice + the unmissable Polymarket banner.
   *  'kalshi' surfaces the queue-preserving decrease nuance and the
   *  force-cancel toggle when the draft would smart-route. 'polymarket'
   *  shows the cancel+place banner at the top of the body and skips the
   *  toggle entirely (there's no smart route to override). */
  venue: 'kalshi' | 'polymarket';
}

function sentenceCase(message: string): string {
  const clean = message.trim().replace(/_/g, ' ');
  if (!clean) return clean;
  const withPeriod = /[.!?]$/.test(clean) ? clean : `${clean}.`;
  return withPeriod.charAt(0).toUpperCase() + withPeriod.slice(1);
}

function extractNestedApiMessage(raw: string): string | null {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  try {
    const parsed = JSON.parse(raw.slice(start, end + 1)) as {
      error?: { message?: unknown; code?: unknown } | string;
      message?: unknown;
      code?: unknown;
    };
    if (typeof parsed.error === 'object' && parsed.error !== null) {
      if (typeof parsed.error.message === 'string') return parsed.error.message;
      if (typeof parsed.error.code === 'string') return parsed.error.code;
    }
    if (typeof parsed.error === 'string') return parsed.error;
    if (typeof parsed.message === 'string') return parsed.message;
    if (typeof parsed.code === 'string') return parsed.code;
  } catch {
    return null;
  }
  return null;
}

function formatReplaceError(err: unknown): string {
  const fallback =
    'Failed to replace order. The original order may have been cancelled — check your open orders.';
  const raw = err instanceof Error && err.message ? err.message : fallback;
  const prefix = 'Original order was cancelled, but the replacement failed to place: ';
  const cancelledOriginal = raw.startsWith(prefix);
  const body = cancelledOriginal ? raw.slice(prefix.length) : raw;
  const apiMessage = extractNestedApiMessage(body);
  const stripped = body.replace(/^Kalshi API \d+:\s*/i, '').trim();
  const detail = sentenceCase(apiMessage ?? stripped);
  return cancelledOriginal ? `${prefix}${detail}` : detail;
}

export function parseContractQuantityText(text: string): number | null {
  const normalized = text.replace(/,/g, '').trim();
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.round(n);
}

function quantityDraftText(quantity: number): string {
  return String(Math.max(1, Math.round(quantity)));
}

function formatUsdAmount(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function EditOrderPriceModal({
  isOpen,
  onClose,
  onReplace,
  marketLabel,
  side,
  action,
  remainingCount,
  currentPriceCents,
  ticker,
  venue,
}: EditOrderPriceModalProps) {
  const feeAdjustedOdds = useDisplaySettings((s) => s.feeAdjustedOdds);
  const feesReady = useFeesReady();
  void feesReady;
  const eventStateCertainty = useOrderEventStateCertainty(ticker);
  const [priceText, setPriceText] = useState(String(currentPriceCents));
  const [qtyText, setQtyText] = useState(quantityDraftText(remainingCount));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Kalshi-only override of the smart route. Off by default — the trader
   *  opts in explicitly when they want to send the order to the back of
   *  the queue. Reset on each modal open so it can't carry over from a
   *  previous edit. */
  const [forceCancelReplace, setForceCancelReplace] = useState(false);

  // Reset draft + clear error each time the modal opens against a new order.
  // currentPriceCents / remainingCount may shift between opens (different
  // order targeted) so we re-seed on isOpen→true.
  useEffect(() => {
    if (isOpen) {
      setPriceText(String(currentPriceCents));
      setQtyText(quantityDraftText(remainingCount));
      setError(null);
      setSubmitting(false);
      setForceCancelReplace(false);
    }
  }, [isOpen, currentPriceCents, remainingCount]);

  // Replacement is CANCEL-FIRST: a tick rejection at the venue lands after
  // the resting order is already gone, so validation here must be provably
  // safe, never permissively guessed (DNL round-3 finding 1). Kalshi's Hydra
  // metadata is discovery-refreshed and can briefly trail a live tick-regime
  // change, so cancel-first replacement intentionally remains whole-cent-only
  // for CHANGED prices (integers are valid across published structures); the
  // unchanged current price always passes. New-order surfaces can safely use
  // the banded priceRanges because a venue rejection there loses no order.
  // Polymarket:
  // resolve the authoritative CLOB tickSize from the store — callers may
  // hand us a market ticker OR an outcome tokenId, so try both; unresolved →
  // price changes disabled. The replace flows revalidate with the same rules
  // immediately before cancellation.
  // Reactive, not a getState() snapshot: hydra can deliver the tickSize while
  // this modal is already open, and the arrow controls must enable themselves
  // when it lands rather than waiting for a reopen (#254).
  const polyTickSize = useMarketStore((s) => {
    if (venue !== 'polymarket' || !ticker) return undefined;
    const direct = s.getMarket(ticker)?.tickSize;
    if (direct) return direct;
    const viaToken = s.getTickerByTokenId(ticker);
    return viaToken ? s.getMarket(viaToken)?.tickSize : undefined;
  });
  const replaceTickArgs = {
    // resolveTickCents, NOT tickCentsFor: a malformed store value must resolve
    // to null and disable replacement, never inherit tickCentsFor's 1¢
    // new-order fallback on a path that cancels before the venue sees us.
    tickCents: venue === 'polymarket' ? resolveTickCents(polyTickSize, undefined) : null,
    unknownTickPolicy: (venue === 'kalshi' ? 'whole-cent' : 'reject') as 'whole-cent' | 'reject',
  };
  const parsedDraft = /^\d+(?:\.\d+)?$/.test(priceText.trim()) ? Number(priceText) : NaN;
  const priceCheck = Number.isFinite(parsedDraft)
    ? validateReplacePriceCents({
        newPriceCents: parsedDraft,
        // The unchanged-price pass is Kalshi-only: a Poly resting order can
        // predate a TICK REGIME CHANGE, so recreating it at the same price
        // is not provably valid — Poly always validates against the
        // resolved tick (DNL final pass).
        currentPriceCents: venue === 'kalshi' ? currentPriceCents : null,
        ...replaceTickArgs,
      })
    : ({ ok: false, error: 'Price must be a number in cents (e.g. 58.3)' } as const);
  const draftCents = priceCheck.ok ? priceCheck.priceCents : null;
  const priceInRange = priceCheck.ok;

  // Arrow controls. Both directions are resolved against the SAME contract the
  // submit gate uses, so an enabled arrow can never yield a price the gate
  // rejects. An unparseable draft (empty field mid-edit) steps from the resting
  // price instead of dying, so the arrows stay usable while typing.
  const stepBase = Number.isFinite(parsedDraft) ? parsedDraft : currentPriceCents;
  const stepContract = {
    currentPriceCents: venue === 'kalshi' ? currentPriceCents : null,
    ...replaceTickArgs,
  };
  const previousPrice = stepReplacePriceCents({
    draftPriceCents: stepBase,
    direction: -1,
    ...stepContract,
  });
  const nextPrice = stepReplacePriceCents({
    draftPriceCents: stepBase,
    direction: 1,
    ...stepContract,
  });
  const applyPriceStep = (target: number | null) => {
    if (target === null || submitting) return;
    setPriceText(String(target));
    // Clears the submit-failure banner only; the inline validation message
    // derives from priceCheck at render time and recomputes on its own.
    setError(null);
  };

  const draftQty = parseContractQuantityText(qtyText);
  const qtyInRange = draftQty !== null && draftQty >= 1;
  const oddsPreview =
    priceInRange && draftCents !== null
      ? americanOdds(
          effectivePriceForFeeAdjustedOdds(
            draftCents,
            venue === 'kalshi' && feeAdjustedOdds,
            makerRateForTicker(ticker),
            0,
          ),
        )
      : '—';
  const riskDollars = priceInRange && qtyInRange && draftCents !== null && draftQty !== null
    ? (draftQty * draftCents) / 100
    : null;
  const toWinDollars = priceInRange && qtyInRange && draftCents !== null && draftQty !== null
    ? (draftQty * ((10000 - Math.round(draftCents * 100)) / 100)) / 100
    : null;

  // "Changed" means either price or qty differs from the current order.
  // Submitting a no-op replace would be wasteful and risk losing queue
  // position for no reason, so the button is disabled in that case.
  const priceChanged = draftCents !== null && draftCents !== currentPriceCents;
  const qtyChanged = draftQty !== null && draftQty !== remainingCount;
  const anyChange = priceChanged || qtyChanged;
  const canSubmit = priceInRange && qtyInRange && anyChange && !submitting;

  // Whether the current draft *would* qualify for the smart route absent
  // any override. Drives the force-toggle's visibility — we only show the
  // checkbox when the trader could opt out of an otherwise-preserving
  // route. Polymarket has no smart route at all so the toggle never shows.
  const couldPreserveQueue =
    venue === 'kalshi' &&
    !priceChanged &&
    draftQty !== null &&
    draftQty < remainingCount;
  const effectiveForceCancelReplace = couldPreserveQueue && forceCancelReplace;

  useEffect(() => {
    if (!couldPreserveQueue && forceCancelReplace) {
      setForceCancelReplace(false);
    }
  }, [couldPreserveQueue, forceCancelReplace]);

  // The actual route the parent will take, factoring in the user's toggle.
  // Drives the green/amber hint copy.
  const willPreserveQueue = couldPreserveQueue && !effectiveForceCancelReplace;

  const handleReplace = async () => {
    if (!canSubmit || draftCents === null || draftQty === null) return;
    setSubmitting(true);
    setError(null);
    try {
      await onReplace(draftCents, draftQty, {
        forceCancelReplace: effectiveForceCancelReplace,
      });
      // Parent owns close-on-success — once the new order lands and
      // refresh completes, the row this modal was anchored to may no
      // longer exist. Parent calls onClose().
    } catch (err) {
      setError(formatReplaceError(err));
      setSubmitting(false);
    }
  };

  const sideLabel = side.toUpperCase();
  const actionLabel = action.toUpperCase();

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => undefined : onClose}
      title="Edit order"
      size="sm"
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="bg-muted hover:bg-muted/80 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => void handleReplace()}
            disabled={!canSubmit}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20',
              'disabled:opacity-50',
            )}
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {submitting ? 'Replacing…' : 'Replace order'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Presentation only: uncertainty never disables replacement or cancel. */}
        <EventStateWarning certainty={eventStateCertainty} />

        {/* Order summary — pure read-only context so the trader can confirm
            they're editing the right order before they commit. */}
        <div className="bg-muted/50 border-border space-y-1 rounded-md border px-3 py-2 text-xs">
          <div className="text-muted-foreground">Editing</div>
          <div className="font-mono">
            <span
              className={cn(
                'font-semibold',
                action === 'buy' ? 'text-positive' : 'text-negative',
              )}
            >
              {actionLabel}
            </span>{' '}
            <span
              className={cn(
                'font-semibold',
                side === 'yes' ? 'text-positive' : 'text-negative',
              )}
            >
              {sideLabel}
            </span>{' '}
            {remainingCount.toLocaleString()} @ {currentPriceCents}¢
          </div>
          <div className="text-muted-foreground truncate">{marketLabel}</div>
        </div>

        {/* Polymarket banner — always cancel + place, no queue preservation.
            Yellow-tinted so it's unmissable at first glance; the existing
            bottom hint repeats the detail for readers who scan top-down.
            Kalshi orders skip this banner because the bottom hint already
            reflects the routing dynamically (green when smart-route, amber
            when cancel+place). */}
        {venue === 'polymarket' && (
          <div
            className="border-yellow-500/40 bg-yellow-500/10 text-yellow-300 light:text-yellow-700 rounded-md border px-3 py-2 text-xs leading-snug"
            role="note"
          >
            <span className="font-semibold">Polymarket: cancel + replace.</span>{' '}
            No queue-preserving decrease exists on Polymarket, so any
            price or qty change cancels the original and places a fresh
            order at the back of the line.
          </div>
        )}

        {/* Price + qty inputs side-by-side. Cents with up to 2 decimals
            (0.01¢ grain — sub-cent ticks are live on both venues); qty is
            integer ≥ 1. Tick misalignment and sub-minimum Polymarket qty
            (< 5) surface as venue rejections from the relay if attempted. */}
        <div className="flex items-end gap-3">
          {/* min-w-0: the stepper's fixed-width buttons raise this cell's
              min-content size, which would otherwise let it outgrow its flex
              share and squeeze the qty column's Risk/To-win line into extra
              wraps. */}
          <div className="min-w-0 flex-1">
            <label className="text-muted-foreground mb-1 block text-xs font-medium">
              New price (¢)
            </label>
            {/* Grouped stepper — same pattern as the main trade ticket's
                price field (GameDetailPanel). The ¢ lives in the label, so
                nothing dangles outside the border box at this width. Error
                state paints the GROUP border, not the input's inner dividers. */}
            <div
              className={cn(
                'border-border bg-input flex rounded-md border',
                priceText !== '' && !priceInRange && 'border-red-500',
              )}
            >
              <button
                type="button"
                aria-label="Previous valid price"
                onClick={() => applyPriceStep(previousPrice)}
                disabled={submitting || previousPrice === null}
                className="text-muted-foreground hover:text-foreground flex w-8 items-center justify-center disabled:opacity-50"
              >
                <Minus size={14} />
              </button>
              <input
                type="text"
                inputMode="decimal"
                value={priceText}
                onChange={(e) => {
                  setPriceText(e.target.value.replace(/[^0-9.]/g, ''));
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    applyPriceStep(previousPrice);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    applyPriceStep(nextPrice);
                  } else if (e.key === 'Enter' && canSubmit) {
                    e.preventDefault();
                    void handleReplace();
                  }
                }}
                disabled={submitting}
                autoFocus
                className="border-border text-foreground focus:border-primary min-w-0 flex-1 border-x border-y-0 bg-transparent px-2 py-1.5 text-center font-mono text-sm focus:outline-none disabled:opacity-50"
                aria-label="New price in cents"
              />
              <button
                type="button"
                aria-label="Next valid price"
                onClick={() => applyPriceStep(nextPrice)}
                disabled={submitting || nextPrice === null}
                className="text-muted-foreground hover:text-foreground flex w-8 items-center justify-center disabled:opacity-50"
              >
                <Plus size={14} />
              </button>
            </div>
            <div className="text-muted-foreground mt-1 text-right font-mono text-[11px]">
              {oddsPreview} American
            </div>
            {priceText !== '' && !priceCheck.ok && (
              <div className="mt-0.5 text-right text-[11px] text-red-400">
                {priceCheck.error}
              </div>
            )}
          </div>
          <div className="flex-1">
            <label className="text-muted-foreground mb-1 block text-xs font-medium">
              New qty
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={qtyText}
              onChange={(e) => {
                setQtyText(e.target.value.replace(/[^\d.,]/g, ''));
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) {
                  e.preventDefault();
                  void handleReplace();
                }
              }}
              disabled={submitting}
              className={cn(
                'border-border bg-input text-foreground focus:border-primary w-full rounded-md border px-3 py-1.5 text-right font-mono text-sm focus:outline-none disabled:opacity-50',
                qtyText !== '' && !qtyInRange && 'border-red-500 focus:border-red-500',
              )}
              aria-label="New quantity"
            />
            <div className="text-muted-foreground mt-1 text-right font-mono text-[11px]">
              Risk {riskDollars === null ? '—' : formatUsdAmount(riskDollars)}
              {' | '}
              To win {toWinDollars === null ? '—' : formatUsdAmount(toWinDollars)}
            </div>
          </div>
        </div>

        {/* Force-cancel toggle (Kalshi-only). Only renders when the current
            draft *would* smart-route to /decrease — i.e. there's actually
            a routing decision to override. Off by default; opt-in for the
            trader who explicitly wants to send the order to the back of
            the queue (the "Cycle" semantic from earlier scoping, folded in
            here as a per-edit toggle instead of a separate menu item). */}
        {couldPreserveQueue && (
          <label className="flex cursor-pointer items-start gap-2 text-xs">
            <input
              type="checkbox"
              checked={effectiveForceCancelReplace}
              onChange={(e) => setForceCancelReplace(e.target.checked)}
              disabled={submitting}
              className="mt-0.5 cursor-pointer"
            />
            <span className="text-foreground select-none">
              Force cancel + replace
              <span className="text-muted-foreground">
                {' '}— bypass /decrease and re-queue at the back. Use when
                you want to deliberately reset queue position.
              </span>
            </span>
          </label>
        )}

        <p className="text-muted-foreground text-xs leading-snug">
          {willPreserveQueue ? (
            <>
              <span className="text-green-400">Queue position preserved</span>{' '}
              — same-price qty shrink routes through Kalshi's /decrease,
              keeping your FIFO slot. Anything else (price change or qty
              increase) would cancel and re-place.
            </>
          ) : couldPreserveQueue && effectiveForceCancelReplace ? (
            <>
              <span className="text-amber-400">
                Forcing cancel + replace
              </span>{' '}
              — you'll lose your FIFO slot and rejoin at the back of the
              queue. Untick the toggle above to fall back to /decrease and
              preserve queue position.
            </>
          ) : venue === 'kalshi' ? (
            <>
              Cancels + re-places at the new price/qty. Brief window
              (under 500ms) where you're uncovered. A same-price qty shrink
              would have preserved queue position via /decrease — set the
              same price + a smaller qty if that's what you want.
            </>
          ) : (
            <>
              Brief uncovered window between the cancel and the new place
              (typically under 500ms). If the place fails after the cancel
              succeeds, the original order is gone and the error below will
              say so.
            </>
          )}
        </p>

        {error && (
          <div
            className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400"
            role="alert"
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default EditOrderPriceModal;
